import { useState } from "react";
import { gr8agenticCapabilities } from "@/data/roadmap";
import { ROADMAP } from "@/constants/testIds/roadmap";
import * as Icons from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles } from "lucide-react";

export default function Gr8Agentic() {
  const [active, setActive] = useState(gr8agenticCapabilities[0].id);
  const cap = gr8agenticCapabilities.find((c) => c.id === active);
  const Icon = Icons[cap.icon] || Icons.Sparkles;

  return (
    <section
      id="gr8agentic"
      data-testid={ROADMAP.gr8agenticSection}
      className="relative py-24 lg:py-32 bg-[#0A0A0A] text-white overflow-hidden border-b border-[#0A0A0A]"
    >
      {/* glowing accent */}
      <div
        aria-hidden
        className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(255,79,0,0.18) 0%, rgba(255,79,0,0) 60%)",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-white/60">
          <span className="font-mono text-[#FF4F00]">/ 04</span>
          <span className="font-semibold">Gr8Agentic AI</span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-[#FF4F00] text-[#FF4F00]">
            <Sparkles size={10} /> AI Co-Pilot
          </span>
        </div>

        <div className="grid lg:grid-cols-12 gap-6 mt-3 items-end">
          <h2 className="lg:col-span-7 font-heading text-3xl md:text-4xl lg:text-5xl tracking-tighter font-bold leading-[1.05]">
            Gr8Agentic AI doesn&apos;t just sit beside your ERP.
            <br />
            <span className="text-[#FF4F00]">It runs alongside it.</span>
          </h2>
          <p className="lg:col-span-5 text-white/70 leading-relaxed text-lg">
            Embedded across every Gr8Boost ERP module — Gr8Agentic AI queries data in plain language, extracts
            documents, forecasts demand, catches anomalies and proposes automations. Always on. Always
            accountable.
          </p>
        </div>

        <div className="mt-14 grid lg:grid-cols-12 gap-0 border border-white/15">
          {/* Tab list */}
          <div className="lg:col-span-4 border-b lg:border-b-0 lg:border-r border-white/15 bg-white/[0.02]">
            {gr8agenticCapabilities.map((c) => {
              const isActive = c.id === active;
              const TIcon = Icons[c.icon] || Icons.Sparkles;
              return (
                <button
                  key={c.id}
                  onClick={() => setActive(c.id)}
                  data-testid={ROADMAP.gr8agenticTab(c.id)}
                  className={`w-full text-left p-5 border-b border-white/10 transition-colors duration-200 ${
                    isActive ? "bg-[#FF4F00] text-white" : "hover:bg-white/5"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <TIcon size={18} className={isActive ? "" : "text-[#FF4F00]"} />
                    <span className="font-heading font-semibold tracking-tight">{c.title}</span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Panel */}
          <div className="lg:col-span-8 p-8 lg:p-12 bg-white/[0.02] min-h-[420px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.25 }}
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 border border-[#FF4F00] flex items-center justify-center">
                    <Icon size={26} className="text-[#FF4F00]" />
                  </div>
                  <h3 className="font-heading text-2xl md:text-3xl tracking-tighter font-bold">
                    {cap.title}
                  </h3>
                </div>

                <p className="text-white/80 text-lg leading-relaxed max-w-2xl">
                  {cap.description}
                </p>

                <div className="mt-8 border border-white/15 bg-black/40 p-6 font-mono text-sm">
                  <div className="text-xs uppercase tracking-[0.2em] text-[#FF4F00] mb-3">
                    Example
                  </div>
                  <div className="text-white/90 leading-relaxed">{cap.example}</div>
                </div>

                <div className="mt-8 flex items-center gap-3 text-xs uppercase tracking-[0.15em] text-white/50">
                  <span className="font-mono text-[#FF4F00]">▸</span>
                  Embedded across all 8 Gr8Boost ERP modules
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
